import React, {useState} from 'react';

import {Button, Collapse, SplitButton} from '@kit';

import {cls} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import type {ActionEntry} from '../model/action-list';
import type {AttackDamage} from '../model/attacks';
import type {CharacterSpell, SpellCasting} from '../model/collect-effects';
import {formatDice} from '../model/dice';
import {abilityLabel, castingTimeLabel, saveLabel, weaponPropertyLabel} from '../model/labels';
import {ActiveSpellToggle} from './active-spell-toggle';
import {FeatureActionCells} from './feature-actions/feature-action-cells';
import {SpellTitleIcons} from './spell-title-icons';
import {RollButton} from './dice-roller';
import {ExpandButton, expandOnClick, RowDescription, RowDescriptionView} from './expandable-row';
import {byTitle, formatBonus, formatRange} from './format';
import {DamageTypeIcon, DamageTypeIcons} from './damage-type-icon';
import {Measure} from './measure';
import {slotTitle, useSpellCasting} from './use-spell-casting';
import {UsageCount} from './usage-tracker';

import css from './sheet.module.css';

export type ActionTableGroup = {
	key: string;
	title: React.ReactNode;
	/** справа от заголовка группы: ячейки круга */
	aside?: React.ReactNode;
	/** строка под заголовком: стандартные действия */
	intro?: React.ReactNode;
	entries: ActionEntry[];
};

type Props = {
	groups: ActionTableGroup[];
	/** колонка длительности перед дистанцией: для вкладки заклинаний */
	withCastingTime?: boolean;
};

type CastLabelProps = {
	full: string;
	/** подпись для таблицы уже 450px */
	short: string;
	/** уточнение после подписи: видно в таблице от 600px */
	suffix?: string;
};

const CastLabel: React.FC<CastLabelProps> = ({full, short, suffix}) => (
	<>
		<span className={css.castFull}>
			{full}
			{suffix && <span className={css.tableWideOnly}>, {suffix}</span>}
		</span>
		<span className={css.castShort} title={full}>
			{short}
		</span>
	</>
);

export const ActionTable: React.FC<Props> = ({groups, withCastingTime}) => {
	const columnCount = withCastingTime ? 6 : 5;
	return (
		<div className={css.tableScroll}>
			<table className={`${css.actionTable} ${css.actionsTable} ${withCastingTime ? css.spellsTable : ''}`}>
				<thead>
					<tr>
						<th>Название</th>
						{withCastingTime && (
							<th className={css.wrapHeader} data-col="castingTime">
								<span className={css.tableWideOnly}>
									Время
									<br />
									сотворения
								</span>
								<span className={css.tableNarrowOnly}>Время сотв.</span>
							</th>
						)}
						<th data-col="range">Дистанция</th>
						<th data-col="attack">Атака / СЛ</th>
						<th data-col="damage">Урон</th>
						<th data-col="notes">Примечания</th>
					</tr>
				</thead>
				{groups.map((group) => (
					<tbody key={group.key}>
						<tr className={css.groupRow}>
							<th colSpan={columnCount}>
								<span className={css.groupRowContent}>
									<span>{group.title}</span>
									{group.aside}
								</span>
							</th>
						</tr>
						{group.intro && (
							<tr>
								<td colSpan={columnCount}>{group.intro}</td>
							</tr>
						)}
						{[...group.entries]
							.sort((first, second) => byTitle(first.title, second.title))
							.map((entry) => (
								<ActionTableRow
									key={entry.key}
									entry={entry}
									withCastingTime={withCastingTime}
									columnCount={columnCount}
								/>
							))}
					</tbody>
				))}
			</table>
		</div>
	);
};

type RowProps = {
	entry: ActionEntry;
	withCastingTime?: boolean;
	columnCount: number;
};

const ActionTableRow: React.FC<RowProps> = ({entry, withCastingTime, columnCount}) => {
	const {character} = useCharacter();
	const [isOpen, setOpen] = useState(false);
	const feature = entry.featureKey ? character.getFeatureInstance(entry.featureKey)?.feature : undefined;
	const description: RowDescription | undefined = entry.spellId
		? {kind: 'spell', id: entry.spellId}
		: entry.glossaryId
		? {kind: 'glossary', id: entry.glossaryId}
		: entry.attack?.itemId
		? {kind: 'item', id: entry.attack.itemId}
		: feature?.description
		? {kind: 'text', text: feature.description}
		: undefined;

	const nameCell = (
		<td
			className={cls(css.nameCell, description && css.expandable)}
			onClick={description ? expandOnClick(() => setOpen(!isOpen)) : undefined}
		>
			<span className={css.nameCellContent}>
				{description ? (
					<ExpandButton isOpen={isOpen} onToggle={() => setOpen(!isOpen)} />
				) : (
					<span className={css.expandPlaceholder} />
				)}
				<span>
					<span className={css.rowTitle}>
						{entry.title}
						{entry.spellId && <SpellTitleIcons spellId={entry.spellId} asRitual={entry.isRitual} />}
					</span>
					{entry.note && <span className={css.cellNote}>{entry.note}</span>}
					{entry.useKey && <UsageCount useKey={entry.useKey} />}
				</span>
			</span>
		</td>
	);

	return (
		<>
			<tr className={css.actionRow}>
				{nameCell}
				{entry.spellCasting ? (
					<SpellCells
						spell={entry.spellCasting.spell}
						casting={entry.spellCasting.casting}
						withCastingTime={withCastingTime}
					/>
				) : entry.featureAction ? (
					<FeatureActionCells action={entry.featureAction} withCastingTime={withCastingTime} />
				) : (
					<AttackCells entry={entry} withCastingTime={withCastingTime} />
				)}
			</tr>
			{description && (
				<Collapse
					open={isOpen}
					wrapper={(content) => (
						<tr>
							<td colSpan={columnCount} className={css.descriptionCell}>
								{content}
							</td>
						</tr>
					)}
				>
					<RowDescriptionView description={description} />
				</Collapse>
			)}
		</>
	);
};

const TABLE_BUTTON = {appearance: 'secondary', size: 'small'} as const;

/** кнопка броска атаки: почти квадратная, в ней только бонус */
const ATTACK_BUTTON = {...TABLE_BUTTON, className: css.rollSquare} as const;

const DamageLabel: React.FC<{damage: AttackDamage; suffix?: string}> = ({damage: {formula, type}, suffix}) => (
	<span className={css.damageLabel}>
		{formatDice(formula)}
		{type && <DamageTypeIcon type={type} />}
		{suffix}
	</span>
);

const AttackCells: React.FC<{entry: ActionEntry; withCastingTime?: boolean}> = ({entry, withCastingTime}) => {
	const {attack} = entry;
	if (!attack) {
		return (
			<>
				{withCastingTime && <td />}
				<td />
				<td />
				<td />
				<td />
			</>
		);
	}
	const notes = [
		...attack.properties.map(weaponPropertyLabel),
		attack.mastery && `мастерство: ${attack.mastery}`,
	].filter(Boolean);

	return (
		<>
			{withCastingTime && <td />}
			<td>
				<Measure text={formatRange(attack.range)} />
			</td>
			<td>
				<RollButton
					title={`${attack.title}: атака`}
					bonus={attack.attackBonus}
					context={{kind: 'attack'}}
					{...ATTACK_BUTTON}
				>
					{formatBonus(attack.attackBonus)}
				</RollButton>
			</td>
			<td>
				<span className={css.cellButtons}>
					<RollButton title={`${attack.title}: урон`} formula={attack.damage.formula} {...TABLE_BUTTON}>
						<DamageLabel damage={attack.damage} />
					</RollButton>
					{attack.versatileDamage && (
						<RollButton
							title={`${attack.title}: урон двумя руками`}
							formula={attack.versatileDamage.formula}
							{...TABLE_BUTTON}
						>
							<DamageLabel damage={attack.versatileDamage} suffix="двумя руками" />
						</RollButton>
					)}
				</span>
			</td>
			<td className={css.notesCell}>{notes.join(', ')}</td>
		</>
	);
};

type SpellCellsProps = {
	spell: CharacterSpell;
	casting: SpellCasting;
	withCastingTime?: boolean;
};

const SpellCells: React.FC<SpellCellsProps> = ({spell, casting, withCastingTime}) => {
	const {data, slots, slot, setSlotKey, stats, dice, isFree, canCast, hasAttack, cast} = useSpellCasting(
		spell,
		casting,
	);
	if (!data) return null;

	const isHealing = data.dice?.kind === 'healing';
	const diceLabel = dice && (
		<span className={css.damageLabel}>
			{formatDice(dice)}
			{isHealing ? ' лечение' : <DamageTypeIcons types={data.damageTypes} />}
		</span>
	);
	const components = [
		data.components.verbal && 'С',
		data.components.somatic && 'Ж',
		data.components.material && 'М',
	].filter(Boolean);
	const notes = [components.join(', '), data.durationText].filter(Boolean);
	const castingTime = data.castingTime && castingTimeLabel(data.castingTime.unit, data.castingTime.amount);
	const castLabel = !canCast ? (
		casting.kind === 'slot' ? (
			'Нет ячеек'
		) : (
			<CastLabel full="Использовано" short="Исп." />
		)
	) : (
		<CastLabel full="Сотворить" short="Сотв." suffix={slot ? `${slot.level} круг` : undefined} />
	);

	return (
		<>
			{withCastingTime && <td title={castingTime?.title}>{castingTime?.short}</td>}
			<td>
				<Measure text={data.rangeText} />
			</td>
			<td>
				<span className={css.cellButtons}>
					{!isFree &&
						(slots.length ? (
							<SplitButton
								size="small"
								buttonClassName={css.castButton}
								disabled={!canCast}
								menuLabel={`Круг ячейки: ${data.title}`}
								items={slots.map((candidate) => ({
									value: candidate.key,
									label: `${slotTitle(candidate)} (${candidate.max - candidate.spent})`,
								}))}
								value={slot?.key}
								onChange={(value) => setSlotKey(String(value))}
								onClick={cast}
							>
								{castLabel}
							</SplitButton>
						) : (
							<Button size="small" className={css.castButton} disabled={!canCast} onClick={cast}>
								{castLabel}
							</Button>
						))}
					{hasAttack && (
						<RollButton
							title={`${data.title}: атака`}
							bonus={stats.attackBonus}
							context={{kind: 'attack'}}
							{...ATTACK_BUTTON}
						>
							{formatBonus(stats.attackBonus!)}
						</RollButton>
					)}
					{!hasAttack && data.savingThrows.length > 0 && stats.saveDifficulty != null && (
						<span
							title={`Испытание: ${data.savingThrows.map(abilityLabel).join(' или ')}, СЛ ${
								stats.saveDifficulty
							}`}
						>
							{saveLabel(data.savingThrows, stats.saveDifficulty)}
						</span>
					)}
				</span>
			</td>
			<td>
				{dice && diceLabel && (
					<RollButton
						title={`${data.title}: ${isHealing ? 'лечение' : 'урон'}`}
						formula={dice}
						{...TABLE_BUTTON}
					>
						{diceLabel}
					</RollButton>
				)}
			</td>
			<td className={css.notesCell}>
				<span title={data.components.material}>{notes.join(' · ')}</span>
				<ActiveSpellToggle spellId={spell.id} />
			</td>
		</>
	);
};
